import React, { useState, useEffect, useRef } from 'react';
import { 
  Zap, 
  Settings, 
  Users, 
  Activity, 
  TrendingUp, 
  TrendingDown, 
  Minus, 
  RefreshCw,
  AlertTriangle,
  CheckCircle,
  Clock,
  BarChart3
} from 'lucide-react';
import { autoscalingService, AutoscalingStatus } from '../services/autoscaling';

interface AutoscalingSettingsProps {
  className?: string;
}

export function AutoscalingSettings({ className = '' }: AutoscalingSettingsProps) {
  const [status, setStatus] = useState<AutoscalingStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [triggering, setTriggering] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdate, setLastUpdate] = useState<Date>(new Date());

  // Refs to track mounted state and ongoing operations
  const isMountedRef = useRef(true);
  const loadingControllerRef = useRef<AbortController | null>(null);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  // Safe state setter that only updates if component is still mounted
  const safeSetState = <T,>(setter: React.Dispatch<React.SetStateAction<T>>, value: T) => {
    if (isMountedRef.current) {
      setter(value);
    }
  };

  // Load autoscaling status with proper error handling and cancellation
  const loadStatus = async (signal?: AbortSignal) => {
    try {
      console.log('[AutoscalingSettings] Loading status...');
      
      // Cancel previous request if still pending
      if (loadingControllerRef.current) {
        loadingControllerRef.current.abort();
      }
      
      // Create new abort controller for this request
      const controller = new AbortController();
      loadingControllerRef.current = controller;
      
      // Use provided signal or create combined signal
      const combinedSignal = signal || controller.signal;
      
      if (combinedSignal.aborted) {
        console.log('[AutoscalingSettings] Request aborted before fetch');
        return;
      }

      const currentStatus = await autoscalingService.getStatus();
      
      // Check if request was aborted during fetch
      if (combinedSignal.aborted) {
        console.log('[AutoscalingSettings] Request aborted after fetch');
        return;
      }

      console.log('[AutoscalingSettings] Loaded autoscaling status:', currentStatus);
      
      // Only update state if component is still mounted and request wasn't aborted
      if (isMountedRef.current && !combinedSignal.aborted) {
        safeSetState(setStatus, currentStatus);
        safeSetState(setError, null);
        safeSetState(setLastUpdate, new Date());
      }
    } catch (err) {
      console.error('[AutoscalingSettings] Error loading autoscaling status:', err);
      
      // Only update error state if not aborted and component is mounted
      if (isMountedRef.current && !signal?.aborted) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to load autoscaling status';
        safeSetState(setError, errorMessage);
      }
    } finally {
      // Clear the controller reference
      if (loadingControllerRef.current) {
        loadingControllerRef.current = null;
      }
      
      // Only update loading state if component is still mounted
      if (isMountedRef.current && !signal?.aborted) {
        safeSetState(setLoading, false);
      }
    }
  };

  // Toggle autoscaling enabled/disabled with proper error handling
  const toggleAutoscaling = async (enabled: boolean) => {
    if (!isMountedRef.current) return;

    safeSetState(setSaving, true);
    safeSetState(setError, null);
    
    try {
      console.log('[AutoscalingSettings] Toggling autoscaling:', enabled);
      
      await autoscalingService.setEnabled(enabled);
      
      // Wait a moment for backend to update
      await new Promise(resolve => setTimeout(resolve, 500));
      
      // Reload status if component is still mounted
      if (isMountedRef.current) {
        await loadStatus();
      }
    } catch (err) {
      console.error('[AutoscalingSettings] Error toggling autoscaling:', err);
      
      if (isMountedRef.current) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to update autoscaling setting';
        safeSetState(setError, errorMessage);
      }
    } finally {
      if (isMountedRef.current) {
        safeSetState(setSaving, false);
      }
    }
  };

  // Manually trigger autoscaling with proper error handling
  const triggerManual = async () => {
    if (!isMountedRef.current || !status?.enabled) {
      if (!status?.enabled) {
        safeSetState(setError, 'Autoscaling must be enabled to trigger manually');
      }
      return;
    }

    safeSetState(setTriggering, true);
    safeSetState(setError, null);
    
    try {
      console.log('[AutoscalingSettings] Triggering manual autoscaling...');
      
      const result = await autoscalingService.triggerManual();
      console.log('[AutoscalingSettings] Autoscaling result:', result);
      
      if (!isMountedRef.current) return;
      
      // Show success message
      try {
        if (result.result?.decision) {
          const decision = result.result.decision;
          const message = `Autoscaling triggered successfully!\n\nAction: ${decision.action}\nReason: ${decision.reason}\nTarget Workers: ${decision.targetWorkerCount}\nTarget Concurrency: ${decision.targetConcurrency}`;
          
          // Use setTimeout to avoid blocking the UI
          setTimeout(() => {
            if (isMountedRef.current) {
              alert(message);
            }
          }, 100);
        } else if (result.result?.skipped) {
          const message = `Autoscaling check completed.\n\n${result.result.reason}`;
          setTimeout(() => {
            if (isMountedRef.current) {
              alert(message);
            }
          }, 100);
        } else {
          setTimeout(() => {
            if (isMountedRef.current) {
              alert('Autoscaling triggered successfully!');
            }
          }, 100);
        }
      } catch (alertError) {
        console.error('[AutoscalingSettings] Error showing alert:', alertError);
      }
      
      // Wait for backend to update
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // Reload status multiple times to ensure we get latest data
      if (isMountedRef.current) {
        try {
          await loadStatus();
          await new Promise(resolve => setTimeout(resolve, 500));
          if (isMountedRef.current) {
            await loadStatus();
          }
        } catch (reloadError) {
          console.error('[AutoscalingSettings] Error reloading status after trigger:', reloadError);
        }
      }
    } catch (err) {
      console.error('[AutoscalingSettings] Error triggering manual autoscaling:', err);
      
      if (isMountedRef.current) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to trigger autoscaling';
        safeSetState(setError, errorMessage);
        
        // Show error alert
        try {
          setTimeout(() => {
            if (isMountedRef.current) {
              alert(`Error: ${errorMessage}`);
            }
          }, 100);
        } catch (alertError) {
          console.error('[AutoscalingSettings] Error showing error alert:', alertError);
        }
      }
    } finally {
      if (isMountedRef.current) {
        safeSetState(setTriggering, false);
      }
    }
  };

  // Setup and cleanup effects
  useEffect(() => {
    console.log('[AutoscalingSettings] Component mounted');
    isMountedRef.current = true;
    
    // Initial load
    loadStatus();
    
    // Setup auto-refresh interval
    intervalRef.current = setInterval(() => {
      if (isMountedRef.current) {
        loadStatus();
      }
    }, 30000); // 30 seconds

    // Cleanup function
    return () => {
      console.log('[AutoscalingSettings] Component unmounting');
      isMountedRef.current = false;
      
      // Cancel any ongoing requests
      if (loadingControllerRef.current) {
        loadingControllerRef.current.abort();
        loadingControllerRef.current = null;
      }
      
      // Clear interval
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, []); // Empty dependency array - only run once

  // Render loading state
  if (loading) {
    return (
      <div className={`bg-white rounded-lg border border-gray-200 p-6 ${className}`}>
        <div className="animate-pulse" data-testid="loading-container">
          <div className="h-6 bg-gray-200 rounded w-1/3 mb-4"></div>
          <div className="space-y-3">
            <div className="h-4 bg-gray-200 rounded w-full"></div>
            <div className="h-4 bg-gray-200 rounded w-2/3"></div>
          </div>
        </div>
      </div>
    );
  }

  const getActionIcon = (action?: string) => {
    switch (action) {
      case 'scale_up':
        return <TrendingUp className="h-4 w-4 text-green-600" />;
      case 'scale_down':
        return <TrendingDown className="h-4 w-4 text-blue-600" />;
      case 'maintain':
        return <Minus className="h-4 w-4 text-gray-600" />;
      default:
        return <Activity className="h-4 w-4 text-gray-400" />;
    }
  };

  const getStatusColor = (enabled: boolean, queueDepth: number) => {
    if (!enabled) return 'gray';
    if (queueDepth > 200) return 'red';
    if (queueDepth > 100) return 'yellow';
    return 'green';
  };

  const statusColor = status ? getStatusColor(status.enabled, status.queueDepth) : 'gray';
  const statusColorClasses = {
    gray: 'bg-gray-100 text-gray-800 border-gray-200',
    green: 'bg-green-100 text-green-800 border-green-200',
    yellow: 'bg-yellow-100 text-yellow-800 border-yellow-200',
    red: 'bg-red-100 text-red-800 border-red-200'
  };

  return (
    <div className={`bg-white rounded-lg border border-gray-200 ${className}`}>
      {/* Header */}
      <div className="px-6 py-4 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <Zap className="h-6 w-6 text-blue-600 mr-3" />
            <div>
              <h3 className="text-lg font-medium text-gray-900">Queue Autoscaling</h3>
              <p className="text-sm text-gray-500">
                Automatically adjust AI processing concurrency based on queue demand
              </p>
              <p className="text-xs text-gray-400 mt-1">
                Note: Adjusts processing rate within fixed Cloudflare worker limits
              </p>
            </div>
          </div>
          
          <div className="flex items-center space-x-3">
            {/* Status Badge */}
            <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium border ${statusColorClasses[statusColor]}`}>
              {status?.enabled ? (
                <CheckCircle className="h-4 w-4 mr-1" />
              ) : (
                <AlertTriangle className="h-4 w-4 mr-1" />
              )}
              {status?.enabled ? 'Enabled' : 'Disabled'}
            </span>

            {/* Refresh Button */}
            <button
              onClick={() => loadStatus()}
              disabled={loading}
              className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100"
              title="Refresh status"
            >
              <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            </button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="p-6 space-y-6">
        {/* Error Display */}
        {error && (
          <div className="rounded-md bg-red-50 p-4 border border-red-200">
            <div className="flex">
              <AlertTriangle className="h-5 w-5 text-red-400" />
              <div className="ml-3">
                <h3 className="text-sm font-medium text-red-800">Error</h3>
                <div className="mt-2 text-sm text-red-700">{error}</div>
              </div>
            </div>
          </div>
        )}

        {/* Enable/Disable Toggle */}
        <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
          <div>
            <label htmlFor="autoscaling-enabled" className="text-base font-medium text-gray-900">
              Enable Dynamic Autoscaling
            </label>
            <p className="text-sm text-gray-500">
              Automatically adjust worker count and concurrency based on queue metrics
            </p>
          </div>
          <div className="flex items-center">
            <input
              id="autoscaling-enabled"
              type="checkbox"
              checked={status?.enabled || false}
              onChange={(e) => toggleAutoscaling(e.target.checked)}
              disabled={saving}
              className="h-5 w-5 text-blue-600 focus:ring-blue-500 border-gray-300 rounded disabled:opacity-50"
            />
            {saving && (
              <div className="ml-2 h-4 w-4 border-2 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
            )}
          </div>
        </div>

        {/* Current Status Metrics */}
        {status && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Active Workers */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="flex items-center">
                <Users className="h-5 w-5 text-blue-600 mr-2" />
                <div>
                  <p className="text-sm font-medium text-blue-900">Active Workers</p>
                  <p className="text-2xl font-bold text-blue-700">{status.currentWorkers}</p>
                </div>
              </div>
            </div>

            {/* Current Concurrency */}
            <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
              <div className="flex items-center">
                <Settings className="h-5 w-5 text-purple-600 mr-2" />
                <div>
                  <p className="text-sm font-medium text-purple-900">Concurrency</p>
                  <p className="text-2xl font-bold text-purple-700">{status.currentConcurrency}</p>
                </div>
              </div>
            </div>

            {/* Queue Depth */}
            <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
              <div className="flex items-center">
                <BarChart3 className="h-5 w-5 text-orange-600 mr-2" />
                <div>
                  <p className="text-sm font-medium text-orange-900">Queue Depth</p>
                  <p className="text-2xl font-bold text-orange-700">{status.queueDepth}</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Last Decision */}
        {status?.lastDecision && (
          <div className="border border-gray-200 rounded-lg p-4">
            <h4 className="text-sm font-medium text-gray-900 mb-3">Last Scaling Decision</h4>
            <div className="flex items-start space-x-3">
              {getActionIcon(status.lastDecision.action)}
              <div className="flex-1">
                <div className="flex items-center space-x-2">
                  <span className="text-sm font-medium text-gray-900 capitalize">
                    {status.lastDecision.action.replace('_', ' ')}
                  </span>
                  <span className="text-xs text-gray-500">
                    ({Math.round(status.lastDecision.confidence * 100)}% confidence)
                  </span>
                </div>
                <p className="text-sm text-gray-600 mt-1">{status.lastDecision.reason}</p>
                <div className="flex items-center mt-2 text-xs text-gray-500">
                  <Clock className="h-3 w-3 mr-1" />
                  {new Date(status.lastDecision.timestamp).toLocaleString()}
                </div>
                {status.lastDecision.targetWorkerCount !== status.currentWorkers && (
                  <div className="mt-2 text-xs text-blue-600">
                    Target: {status.lastDecision.targetWorkerCount} workers, {status.lastDecision.targetConcurrency} concurrency
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Manual Trigger */}
        <div className="flex items-center justify-between pt-4 border-t border-gray-200">
          <div>
            <p className="text-sm font-medium text-gray-900">Manual Trigger</p>
            <p className="text-xs text-gray-500">
              Force an immediate autoscaling evaluation and action
            </p>
          </div>
          <button
            onClick={triggerManual}
            disabled={!status?.enabled || triggering}
            className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {triggering ? (
              <div className="h-4 w-4 border-2 border-gray-400 border-t-transparent rounded-full animate-spin mr-2"></div>
            ) : (
              <Zap className="h-4 w-4 mr-2" />
            )}
            {triggering ? 'Triggering...' : 'Trigger Now'}
          </button>
        </div>

        {/* Last Update */}
        <div className="text-xs text-gray-500 text-center pt-2 border-t border-gray-100">
          Last updated: {lastUpdate.toLocaleTimeString()}
        </div>
      </div>
    </div>
  );
}